// --- Helper Constants for TTL (optional, but convenient) ---

import Cookies from "js-cookie";

export const GOLDEN_TICKET_KEY = "goldenTicketLastSeen";

// Use these constants as the third argument to `storage.setItem` for readability.
export const TTL = {
	SECONDS_5: 5 * 1000,
	MINUTES_1: 1 * 60 * 1000,
	MINUTES_5: 5 * 60 * 1000,
	MINUTES_15: 15 * 60 * 1000,
	MINUTES_30: 30 * 60 * 1000,
	HOUR_1: 60 * 60 * 1000,
	HOURS_6: 6 * 60 * 60 * 1000, // Fixed: Was incorrectly set to 6 minutes
	HOURS_12: 12 * 60 * 60 * 1000, // Fixed: Was incorrectly set to 12 minutes
	DAY_1: 24 * 60 * 60 * 1000,
	WEEK_1: 7 * 24 * 60 * 60 * 1000,
	/** Represents no expiry - uses a very large number (approx 10 years) */
	NO_EXPIRY: 10 * 365 * 24 * 60 * 60 * 1000,
};

const clearAllCookies = () => {
	Object.keys(Cookies.get()).forEach(cookieName => {
		Cookies.remove(cookieName);
	});
};

/**
 * Default time-to-live (TTL) for stored items in milliseconds.
 * Default: 1 hours.
 */
const DEFAULT_TTL = TTL.NO_EXPIRY; // 1 hours in ms

/**
 * Interface defining the structure of the item stored in localStorage.
 * It wraps the original value with an expiry timestamp.
 */
interface StoredItem<T> {
	value: T;
	expiry: number; // Timestamp (in milliseconds since epoch) when the item expires
}

/**
 * Checks if localStorage is available and accessible in the current environment.
 * Handles cases like SSR (Server-Side Rendering) or disabled storage.
 * @returns {boolean} True if localStorage is available, false otherwise.
 */
function isLocalStorageAvailable(): boolean {
	// Check if 'window' is defined (ruling out typical Node.js/SSR environments)
	if (typeof window === "undefined") {
		return false;
	}
	// Try accessing localStorage
	try {
		const testKey = "__testLocalStorageAvailability__";
		window.localStorage.setItem(testKey, testKey);
		window.localStorage.removeItem(testKey);
		return true;
	} catch (e) {
		// Log warning if access fails (e.g., storage disabled, quota exceeded during test)
		console.warn("LocalStorage is not available or disabled:", e);
		return false;
	}
}

/**
 * Sets an item in localStorage with an expiry timestamp.
 * The value will be automatically JSON.stringified.
 *
 * @template T - The type of the value being stored.
 * @param {string} key - The key under which to store the value. Must be a string.
 * @param {T} value - The value to store (must be JSON serializable).
 * @param {number} [ttl=DEFAULT_TTL] - Optional time-to-live in milliseconds. Uses DEFAULT_TTL if not provided.
 */
function setItem<T>(key: string, value: T, ttl: number = DEFAULT_TTL): void {
	if (!isLocalStorageAvailable()) {
		console.warn("LocalStorage unavailable: Cannot set item.");
		return; // Do nothing if localStorage is not available
	}
	// Ensure the key is actually a string (basic check)
	if (typeof key !== "string") {
		console.error("LocalStorage setItem Error: Key must be a string.");
		return;
	}

	const now = Date.now();
	// Ensure ttl is a positive number, otherwise use default. Add a minimum TTL perhaps?
	const effectiveTtl = typeof ttl === "number" && ttl > 0 ? ttl : DEFAULT_TTL;
	const expiry = now + effectiveTtl;

	const item: StoredItem<T> = {
		value: value,
		expiry: expiry,
	};

	try {
		window.localStorage.setItem(key, JSON.stringify(item));
		// Optional: console.log(`LocalStorage: Set item '${key}' with expiry ${new Date(expiry).toLocaleString()}`);
	} catch (error) {
		console.error(`LocalStorage setItem Error: Failed setting item "${key}". Quota might be exceeded.`, error);
		// Consider more sophisticated error handling or cleanup logic here if needed
	}
}

/**
 * Gets an item from localStorage, checking its expiry.
 * Automatically parses the JSON string back to its original type.
 * Returns null if the item doesn't exist, is expired, cannot be parsed, or localStorage is unavailable.
 *
 * @template T - The expected type of the value to retrieve.
 * @param {string} key - The key of the item to retrieve. Must be a string.
 * @returns {string | null} The stored value (typed as T), or null if not found, expired, or on error.
 */
function getItem<T>(key: string): T | null {
	if (!isLocalStorageAvailable()) {
		// console.warn("LocalStorage unavailable: Cannot get item."); // Usually too noisy
		return null; // Return null if localStorage is not available
	}
	// Ensure the key is actually a string (basic check)
	if (typeof key !== "string") {
		console.error("LocalStorage getItem Error: Key must be a string.");
		return null;
	}

	try {
		const itemString = window.localStorage.getItem(key);
		if (!itemString) {
			// console.log(`LocalStorage: Item '${key}' not found.`); // Can be noisy
			return null;
		}

		// Attempt to parse the stored string
		let item: StoredItem<T>;
		try {
			item = JSON.parse(itemString);
		} catch (parseError) {
			console.error(
				`LocalStorage getItem Error: Failed to parse item "${key}". Item might be corrupted. Removing.`,
				parseError,
			);
			// Remove potentially corrupted item
			removeItem(key);
			return null;
		}

		// Basic validation of the parsed item structure
		if (typeof item !== "object" || item === null || typeof item.expiry !== "number" || !("value" in item)) {
			console.error(`LocalStorage getItem Error: Parsed item "${key}" has invalid structure. Removing.`);
			// Remove invalid item
			removeItem(key);
			return null;
		}

		const now = Date.now();

		// Check for expiry
		if (now > item.expiry) {
			// console.log(`LocalStorage: Item '${key}' has expired. Removing.`); // Can be noisy
			// Item has expired, remove it (self-cleaning)
			removeItem(key);
			return null;
		}

		// console.log(`LocalStorage: Got item '${key}'.`); // Can be noisy
		return item.value;
	} catch (error) {
		// Catch unexpected errors during retrieval (though most should be caught above)
		console.error(`LocalStorage getItem Error: Unexpected issue getting item "${key}".`, error);
		return null;
	}
}

/**
 * Removes a specific item from localStorage.
 *
 * @param {string} key - The key of the item to remove. Must be a string.
 */
function removeItem(key: string): void {
	if (!isLocalStorageAvailable()) {
		console.warn("LocalStorage unavailable: Cannot remove item.");
		return; // Do nothing if localStorage is not available
	}
	// Ensure the key is actually a string (basic check)
	if (typeof key !== "string") {
		console.error("LocalStorage removeItem Error: Key must be a string.");
		return;
	}

	try {
		window.localStorage.removeItem(key);
		// Optional: console.log(`LocalStorage: Removed item '${key}'.`);
	} catch (error) {
		console.error(`LocalStorage removeItem Error: Failed removing item "${key}".`, error);
	}
}

function makeGoldenTicketCacheKey(agencyId: number, userId: number): string {
	return `${GOLDEN_TICKET_KEY}::agency-${agencyId}::user-${userId}`;
}

/**
 * Clears **all** items from localStorage for the current domain.
 * Use with extreme caution! This will remove items not set by this utility too.
 */
function clearAll(): void {
	if (!isLocalStorageAvailable()) {
		console.warn("LocalStorage unavailable: Cannot clear storage.");
		return; // Do nothing if localStorage is not available
	}
	try {
		const keysToPreserve = new Map();
		for (let i = 0; i < window.localStorage.length; i++) {
			const key = window.localStorage.key(i);
			if (key !== null && key.startsWith(GOLDEN_TICKET_KEY) && window.localStorage.getItem(key) !== null) {
				keysToPreserve.set(key, window.localStorage.getItem(key));
			}
		}

		const preservedPasskeyEmails = window.localStorage.getItem("passkeyEmails");

		window.localStorage.clear();
		console.log("LocalStorage: Cleared all items.");

		localStorage.clear();
		sessionStorage.clear();

		Object.keys(Cookies.get()).forEach(cookieName => Cookies.remove(cookieName));

		if (preservedPasskeyEmails) {
			window.localStorage.setItem("passkeyEmails", preservedPasskeyEmails);
		}

		if (keysToPreserve.size > 0) {
			keysToPreserve.forEach((value, key) => window.localStorage.setItem(key, value));
		}
	} catch (error) {
		console.error("LocalStorage clearAll Error:", error);
	}
}
/**
 * Refreshes the expiry of a given key without changing its value.
 *
 * @template T - The type of the value being stored.
 * @param {string} key - The key to refresh.
 * @param {number} [ttl=DEFAULT_TTL] - New TTL to apply from now.
 */
function refreshItemExpiry<T>(key: string, ttl: number = DEFAULT_TTL): void {
	const value = getItem<T>(key); // This also handles expiry check
	if (value !== null) {
		setItem(key, value, ttl); // Reset the same value with a new expiry
	}
}
function refreshAllExpirableItems(ttl: number = DEFAULT_TTL): void {
	if (!isLocalStorageAvailable()) return;

	for (let i = 0; i < window.localStorage.length; i++) {
		const key = window.localStorage.key(i);
		if (!key) continue;

		try {
			const itemString = window.localStorage.getItem(key);
			if (!itemString) continue;

			const item = JSON.parse(itemString);

			// Only refresh items with correct structure
			if (
				typeof item === "object" &&
				item !== null &&
				"expiry" in item &&
				"value" in item &&
				typeof item.expiry === "number"
			) {
				// Reset with same value, new expiry
				setItem(key, item.value, ttl);
			}
		} catch (err) {
			// Ignore keys that are not managed by our customStorage
			if (err) {
				return;
			}
		}
	}
}

// --- Exporting the utility functions ---

/**
 * A utility object for interacting with localStorage, providing automatic
 * JSON serialization/parsing and item expiration.
 */
export const customStorage = {
	setItem,
	getItem,
	removeItem,
	clearAll,
	refreshItemExpiry,
	refreshAllExpirableItems,
	makeGoldenTicketCacheKey,
};

// Detect if all tabs are closed and then reset localStorage when a new tab opens
function setupBrowserCloseWatcher(resetCallback?: () => void) {
	if (!isLocalStorageAvailable()) return;

	const SESSION_FLAG = "session-alive";
	const HEARTBEAT_KEY = "tab-heartbeat";
	const HEARTBEAT_INTERVAL = 2000; // 2 seconds
	const HEARTBEAT_TTL = 60000; // considered dead if no heartbeat in 5s

	// Set sessionStorage flag to indicate this tab is active
	try {
		sessionStorage.setItem(SESSION_FLAG, "true");
	} catch (error) {
		if (error) {
			return;
		}
	}

	const heartbeat = () => {
		try {
			window.localStorage.setItem(HEARTBEAT_KEY, Date.now().toString());
		} catch (error) {
			if (error) {
				return;
			}
		}
	};

	const checkIfColdStart = () => {
		const last = window.localStorage.getItem(HEARTBEAT_KEY);
		const now = Date.now();
		if (!last || now - parseInt(last, 10) > HEARTBEAT_TTL) {
			// Assume browser was closed and reopened
			console.log("Browser was completely closed. Clearing customStorage.");
			const preservedPasskeyEmails = window.localStorage.getItem("passkeyEmails");
			customStorage.clearAll();
			clearAllCookies();
			if (preservedPasskeyEmails) {
				window.localStorage.setItem("passkeyEmails", preservedPasskeyEmails);
			}
			if (resetCallback) resetCallback();
		}
	};

	// Initial check
	checkIfColdStart();

	// Start heartbeat
	const intervalId = setInterval(heartbeat, HEARTBEAT_INTERVAL);

	// Clean up on unload
	window.addEventListener("beforeunload", () => {
		try {
			sessionStorage.removeItem(SESSION_FLAG);
		} catch (error) {
			if (error) {
				return;
			}
		}
		clearInterval(intervalId);
	});
}

// Export it
export const browserStorageWatcher = {
	setupBrowserCloseWatcher,
};

/**
 * Clears all cookies and localStorage items.
 * This is a utility function to be used in logout or session reset scenarios.
 */
export function clearCookiesAndLocalStorage(): void {
	clearAll();
	clearAllCookies();
}
